import { AppNavItem, Css, Icon, Tokens } from "@homebound/beam";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getStage } from "src/context";
import { useFeatureFlags } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { BeamSideNavLayout } from "src/routing/components/layouts/BeamSideNavLayout";
import * as adminPaths from "src/routing/domains/adminPaths";

/**
 * Admin domain chrome: Beam side nav rail + `<Outlet />` for leaf pages.
 *
 * Role: data router pathless layout. Nest under `BeamNavbarLayout` in `domains/admin.tsx`.
 * Stack: `AppShell` → `BeamNavbarLayout` → `AdminSideNavLayout` → page (`PageHeaderLayout`).
 */
export function AdminSideNavLayout() {
  const { pathname } = useLocation();
  const { featureIsEnabled } = useFeatureFlags();
  const items = useMemo(() => buildAdminNavItems(pathname, featureIsEnabled), [pathname, featureIsEnabled]);

  return <BeamSideNavLayout contrastRail sideNav={{ top: <AdminSideNavHeader />, items }} />;
}

function AdminSideNavHeader() {
  return (
    <div>
      <div css={Css.df.jcc.aic.br8.wPx(48).hPx(48).bgColor(Tokens.SurfaceRaised).color(Tokens.OnSurface).$}>
        <Icon icon="userCircle" />
      </div>
      <h1 css={Css.lg.mt1.color(Tokens.OnSurface).$}>Admin</h1>
    </div>
  );
}

/** A nav link that can optionally be gated behind a feature flag. */
type GatedNavLink = AppNavItem & { featureFlag?: FeatureFlagType };

/** A nav section whose links may each be feature-flag gated. */
type GatedNavSection = { section: true; label: string; items: GatedNavLink[] };

function buildAdminNavItems(pathname: string, featureIsEnabled: (flag: FeatureFlagType) => boolean): AppNavItem[] {
  const sections: GatedNavSection[] = [
    {
      section: true,
      label: "Feature Management",
      items: [
        {
          label: "Global",
          onClick: adminPaths.adminFeatureFlagsRoute.url(),
          active: isNavActive(pathname, adminPaths.adminFeatureFlagsRoute.url()),
        },
        {
          label: "Projects",
          onClick: adminPaths.adminProjectsRoute.url(),
          active: isNavActive(pathname, adminPaths.adminProjectsRoute.url()),
        },
      ],
    },
    {
      section: true,
      label: "User Management",
      items: [
        {
          label: "Users",
          onClick: adminPaths.adminUsersRoute.url(),
          active: isNavActive(pathname, adminPaths.adminUsersRoute.url()),
        },
        {
          label: "User Groups",
          onClick: adminPaths.adminUserGroupsRoute.url(),
          active: isNavActive(pathname, adminPaths.adminUserGroupsRoute.url()),
        },
      ],
    },
    {
      section: true,
      label: "Data Management",
      items: [
        {
          label: "Items",
          onClick: adminPaths.adminItemsRoute.url(),
          active: isNavActive(pathname, adminPaths.adminItemsRoute.url()),
        },
        {
          label: "Cost Codes",
          onClick: adminPaths.adminCostCodesRoute.url(),
          active: isNavActive(pathname, adminPaths.adminCostCodesRoute.url()),
        },
        {
          label: "Locations V2",
          onClick: adminPaths.adminLocationsRoute.url(),
          active: isNavActive(pathname, adminPaths.adminLocationsRoute.url()),
        },
        {
          label: "Feature Types",
          onClick: adminPaths.adminFeatureTypesRoute.url(),
          active: isNavActive(pathname, adminPaths.adminFeatureTypesRoute.url()),
        },
        {
          label: "Finish Schedule",
          onClick: adminPaths.adminFinishScheduleRoute.url(),
          active: isNavActive(pathname, adminPaths.adminFinishScheduleRoute.url()),
          featureFlag: FeatureFlagType.FinishSchedulesV2,
        },
      ],
    },
  ];

  if (getStage() === "qa") {
    sections.push({
      section: true,
      label: "Infrastructure Management",
      items: [
        {
          label: "QA Database",
          onClick: adminPaths.adminInfrastructureRoute.url(),
          active: isNavActive(pathname, adminPaths.adminInfrastructureRoute.url()),
        },
      ],
    });
  }

  // Drop any flag-gated link whose flag is off, then any section left with no links.
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter(({ featureFlag }) => !featureFlag || featureIsEnabled(featureFlag)),
    }))
    .filter((section) => section.items.nonEmpty);
}

function isNavActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}
